(() => {
  'use strict';

  const QUALIFYING_PRODUCT_IDS = [14730282762612];

  const FREE_GIFT_VARIANT_IDS = [
    52494742847860,
    52494710571380
  ];

  const shopRoot = window.Shopify?.routes?.root || '/';

  let validationRunning = false;
  let validationScheduled = null;
  let processingCount = 0;

  function startProcessing() {
    processingCount += 1;
    document.body.classList.add('ms-cart-processing');
  }

  function stopProcessing() {
    processingCount = Math.max(0, processingCount - 1);

    if (processingCount === 0) {
      document.body.classList.remove('ms-cart-processing');
    }
  }

  async function getCart() {
    const response = await fetch(`${shopRoot}cart.js`, {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Unable to retrieve cart: ${response.status}`);
    }

    return response.json();
  }

  function hasQualifyingProduct(cart) {
    return cart.items.some(item =>
      QUALIFYING_PRODUCT_IDS.includes(Number(item.product_id))
    );
  }

  function getGiftItems(cart) {
    return cart.items.filter(item =>
      FREE_GIFT_VARIANT_IDS.includes(Number(item.variant_id))
    );
  }

  async function removeGiftItems(giftItems) {
    const updates = {};

    giftItems.forEach(item => {
      updates[item.key] = 0;
    });

    const response = await fetch(`${shopRoot}cart/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ updates })
    });

    if (!response.ok) {
      throw new Error(
        `Unable to remove free gifts: ${response.status}`
      );
    }

    return response.json();
  }

  async function enforceFreeGiftRule(cartData = null) {
    if (validationRunning) {
      return {
        valid: true,
        skipped: true,
        giftsRemoved: false
      };
    }

    validationRunning = true;

    try {
      const cart = cartData || await getCart();
      const giftItems = getGiftItems(cart);

      if (hasQualifyingProduct(cart) || !giftItems.length) {
        return {
          valid: true,
          giftsRemoved: false,
          cart
        };
      }

      const updatedCart = await removeGiftItems(giftItems);

      document.dispatchEvent(
        new CustomEvent('free-gift-guard:removed', {
          detail: {
            removedItems: giftItems,
            cart: updatedCart
          }
        })
      );

      return {
        valid: false,
        giftsRemoved: true,
        removedItems: giftItems,
        cart: updatedCart
      };
    } catch (error) {
      console.error('[Free Gift Guard]', error);

      return {
        valid: false,
        giftsRemoved: false,
        error
      };
    } finally {
      validationRunning = false;
    }
  }

  function reloadCorrectedCart() {
    window.location.reload();
  }

  function scheduleValidation(delay = 500) {
    window.clearTimeout(validationScheduled);

    validationScheduled = window.setTimeout(async () => {
      startProcessing();

      try {
        const result = await enforceFreeGiftRule();

        if (result.giftsRemoved) {
          reloadCorrectedCart();
          return;
        }
      } finally {
        stopProcessing();
      }
    }, delay);
  }

  function monitorFetchRequests() {
    if (window.__freeGiftGuardFetchInstalled) return;

    window.__freeGiftGuardFetchInstalled = true;

    const originalFetch = window.fetch;

    window.fetch = async function (...args) {
      const request = args[0];
      const requestUrl =
        request instanceof Request ? request.url : String(request);

      const isCartMutation =
        /\/cart\/(add|change|update|clear)(\.js)?(?:\?|$)/i.test(
          requestUrl
        );

      if (!isCartMutation) {
        return originalFetch.apply(this, args);
      }

      startProcessing();

      try {
        const response = await originalFetch.apply(this, args);

        if (response.ok && !validationRunning) {
          try {
            const updatedCart = await response.clone().json();

            if (updatedCart && Array.isArray(updatedCart.items)) {
              const result = await enforceFreeGiftRule(updatedCart);

              if (result.giftsRemoved) {
                reloadCorrectedCart();
              }
            }
          } catch (error) {
            console.error(
              '[Free Gift Guard] Cart validation failed:',
              error
            );
          }
        }

        return response;
      } finally {
        stopProcessing();
      }
    };
  }

  function monitorCartControls() {
    document.addEventListener('click', event => {
      const cartControl = event.target.closest(
        [
          'cart-remove-button',
          '[data-cart-remove]',
          '[data-remove-item]',
          '.cart-remove-button',
          '.quantity__button',
          '[name="minus"]',
          '[name="plus"]'
        ].join(',')
      );

      if (cartControl) {
        scheduleValidation(800);
      }
    });
  }

  async function initialValidation() {
    startProcessing();

    try {
      const result = await enforceFreeGiftRule();

      if (result.giftsRemoved) {
        reloadCorrectedCart();
        return;
      }
    } finally {
      stopProcessing();
    }
  }

  function initialize() {
    monitorFetchRequests();
    monitorCartControls();
    initialValidation();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, {
      once: true
    });
  } else {
    initialize();
  }

  window.addEventListener('pageshow', event => {
    if (event.persisted) {
      scheduleValidation(100);
    }
  });
})();